import FormBlock from "@common/controls/Form/Block/component";
import HelpIcon from "@common/controls/Icon/Help/component";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { formatMoneyDisplay } from "@common/lib/format";
import { usePromise } from "@common/lib/hooks";
import { i18nFormat } from "@common/lib/i18n";
import Network from "@common/modules/network/types";
import { getUserDisplayName } from "@common/modules/user/lib";
import { groupBy, sumBy, values } from "lodash";
import * as React from "react";
import { type Affiliate } from "../types";
import CommissionAuditModal from "./Modal/CommissionAuditModal/component";

type Props = {
  api: Affiliate.AuditApi;
  cycleEndDate: string;
  getPayoutCycles: (data: Network.Payouts.Api.GetCyclesRequest) => Promise<Network.Payouts.Api.Cycle[]>;
  style?: React.CSSProperties;
};
export const CommissionsWidget = (props: Props) => {
  const i18n = useI18n();

  const [currentRuleId, setCurrentRuleId] = React.useState<string | null>(null);

  const [cycles, isCyclesLoading] = usePromise<Network.Payouts.Api.Cycle[]>({
    fn: props.getPayoutCycles,
    args: [
      {
        cycleEndDate: props.cycleEndDate
      }
    ],
    shouldLoad: !!props.cycleEndDate
  });

  const groupedCycles = React.useMemo(
    () =>
      sort(
        values(groupBy(cycles, (x) => x.commissionRuleId)).map((x) => ({
          label: x[0].commissionRuleName,
          value: x[0].commissionRuleId,
          amount: sumBy(x, (y) => y.amount),
          shares: sumBy(x, (y) => y.shares),
          currencyCode: x[0].currencyCode
        })),
        {
          key: "label",
          order: "asc"
        }
      ),
    [cycles]
  );

  return (
    <FormBlock
      className="affiliate_commissions_block"
      isNarrow
      isLoading={isCyclesLoading}
      style={props.style}
      title={i18n.commissions}
    >
      {cycles?.length ? (
        <>
          <ul className="items">
            {groupedCycles.map((x) => (
              <li key={x.value} className="item" onClick={() => setCurrentRuleId(x.value)}>
                <span>{x.label}</span>
                <span className="fr fg sm">
                  {x.shares > 0 && (
                    <HelpIcon
                      tooltip={i18nFormat(
                        i18n.fe_SharesKpiHelp,
                        ["NUMBER", x.shares ?? 0],
                        [
                          "PRICE",
                          formatMoneyDisplay(x.amount / x.shares, x.currencyCode, { digits: 4, trimZero: true })
                        ]
                      )}
                    />
                  )}

                  {formatMoneyDisplay(x.amount, x.currencyCode)}
                </span>
              </li>
            ))}
          </ul>
          <div className="item total">
            <span>{i18n.total}</span>
            <span>
              {formatMoneyDisplay(
                sumBy(cycles, (x) => x.amount),
                cycles[0].currencyCode
              )}
            </span>
          </div>
        </>
      ) : (
        <div className="no_data">{i18n.infoNoCommissions}</div>
      )}

      {currentRuleId && !!cycles?.length && (
        <CommissionAuditModal
          cycle={{
            ...cycles[0],
            commissionRuleId: currentRuleId,
            genealogyId: undefined,
            userId: undefined,
            userFullName: getUserDisplayName(cycles[0])
          }}
          className="commission_audit"
          api={props.api}
          onClose={() => setCurrentRuleId(null)}
        />
      )}
    </FormBlock>
  );
};
